import { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Filter,
  Calendar,
  Download,
  RefreshCw,
  AlertCircle,
  Info,
  CheckCircle,
  XCircle,
  Activity,
  Clock,
  User,
} from 'lucide-react';
import {
  AuditLog,
  AuditLogCategory,
  AuditLogAction,
  AuditLogSeverity,
  AUDIT_CATEGORY_INFO,
  AUDIT_ACTION_INFO,
  AUDIT_SEVERITY_INFO,
} from '../types/auditLog';
import { auditService } from '../services/auditService';

const normalizeAuditLog = (row: any): AuditLog => {
  let changes: any = row.changes || undefined;
  if (typeof changes === 'string') {
    try { changes = JSON.parse(changes); } catch { changes = undefined; }
  }

  let metadata: Record<string, any> | undefined = row.metadata || undefined;
  if (typeof metadata === 'string') {
    try { metadata = JSON.parse(metadata); } catch { metadata = undefined; }
  }

  return {
    id: String(row.id),
    timestamp: row.logged_at || row.created_at || row.timestamp || new Date().toISOString(),
    category: String(row.category || 'SYSTEM') as AuditLogCategory,
    action: String(row.action || 'UPDATE') as AuditLogAction,
    severity: String(row.severity || 'info') as AuditLogSeverity,
    userId: String(row.user_id || row.userId || ''),
    userName: row.user_name || row.userName || 'Sistem',
    userRole: row.user_role || row.userRole || 'system',
    entity: row.entity || '',
    entityId: row.entity_id != null ? String(row.entity_id) : undefined,
    entityName: row.entity_name || undefined,
    description: row.description || '',
    changes: Array.isArray(changes) ? changes : undefined,
    metadata,
    ipAddress: row.ip_address || undefined,
    userAgent: row.user_agent || undefined,
    success: row.success === undefined ? true : Boolean(row.success),
    errorMessage: row.error_message || undefined,
  };
};

const getCategoryInfo = (category: string) => AUDIT_CATEGORY_INFO[category as AuditLogCategory] || { label: category, color: 'bg-zinc-100 text-zinc-800', icon: '' };
const getActionInfo = (action: string) => AUDIT_ACTION_INFO[action as AuditLogAction] || { label: action, color: 'text-gray-600' };

const filterAuditLogs = (logs: AuditLog[], filter: {
  category?: AuditLogCategory[];
  action?: AuditLogAction[];
  severity?: AuditLogSeverity[];
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
}) => logs.filter((log) => {
  if (filter.category?.length && !filter.category.includes(log.category)) return false;
  if (filter.action?.length && !filter.action.includes(log.action)) return false;
  if (filter.severity?.length && !filter.severity.includes(log.severity)) return false;
  if (filter.startDate && log.timestamp < filter.startDate) return false;
  if (filter.endDate && log.timestamp > `${filter.endDate}T23:59:59`) return false;
  if (filter.searchQuery) {
    const query = filter.searchQuery.toLocaleLowerCase('tr-TR');
    return [log.description, log.userName, log.entity, log.entityName, log.entityId, log.ipAddress]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase('tr-TR').includes(query));
  }
  return true;
});

const calculateStats = (logs: AuditLog[]) => {
  const now = new Date();
  const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return {
    total: logs.length,
    last24h: logs.filter((log) => new Date(log.timestamp) > last24h).length,
    last7d: logs.filter((log) => new Date(log.timestamp) > last7d).length,
    failures: logs.filter((log) => !log.success).length,
    byCategory: logs.reduce((acc, log) => ({ ...acc, [log.category]: (acc[log.category] || 0) + 1 }), {} as Record<string, number>),
    bySeverity: logs.reduce((acc, log) => ({ ...acc, [log.severity]: (acc[log.severity] || 0) + 1 }), {} as Record<string, number>),
  };
};

const getDefaultDateRange = () => {
  const today = new Date();
  const toDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const value = toDateInput(today);
  return { start: value, end: value };
};

export function SystemLogs() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<AuditLogCategory[]>([]);
  const [selectedActions, setSelectedActions] = useState<AuditLogAction[]>([]);
  const [selectedSeverity, setSelectedSeverity] = useState<AuditLogSeverity[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(getDefaultDateRange);
  const [showFilters, setShowFilters] = useState(false);
  const [allLogs, setAllLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });

  const loadLogs = async (range = dateRange) => {
    try {
      setLoading(true);
      setLoadError('');
      const response: any = await auditService.getLogs({
        startDate: range.start ? `${range.start}T00:00:00` : undefined,
        endDate: range.end ? `${range.end}T23:59:59` : undefined,
        page,
        pageSize: 100,
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setAllLogs(rows.map(normalizeAuditLog));
      setPagination({
        total: Number(response?.pagination?.total) || 0,
        totalPages: Number(response?.pagination?.totalPages) || 0,
      });
    } catch (error) {
      console.error('Audit logs load failed:', error);
      setAllLogs([]);
      setPagination({ total: 0, totalPages: 0 });
      setLoadError('Loglar yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLogs();
  }, [dateRange.start, dateRange.end, page]);

  // Filtrelenmiş loglar
  const logs = useMemo(() => {
    return filterAuditLogs(allLogs, {
      category: selectedCategories.length > 0 ? selectedCategories : undefined,
      action: selectedActions.length > 0 ? selectedActions : undefined,
      severity: selectedSeverity.length > 0 ? selectedSeverity : undefined,
      startDate: dateRange.start || undefined,
      endDate: dateRange.end || undefined,
      searchQuery: searchQuery || undefined,
    });
  }, [allLogs, searchQuery, selectedCategories, selectedActions, selectedSeverity, dateRange]);

  // İstatistikler
  const stats = useMemo(() => calculateStats(allLogs), [allLogs]);

  // Kategori toggle
  const toggleCategory = (category: AuditLogCategory) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Aksiyon toggle
  const toggleAction = (action: AuditLogAction) => {
    setSelectedActions(prev =>
      prev.includes(action)
        ? prev.filter(a => a !== action)
        : [...prev, action]
    );
  };

  // Severity toggle
  const toggleSeverity = (severity: AuditLogSeverity) => {
    setSelectedSeverity(prev =>
      prev.includes(severity)
        ? prev.filter(s => s !== severity)
        : [...prev, severity]
    );
  };

  // Logları dışa aktar
  const handleExport = () => {
    const data = JSON.stringify(logs, null, 2);

    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Tarih formatlama
  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  // Severity ikonu
  const getSeverityIcon = (severity: AuditLogSeverity) => {
    switch (severity) {
      case 'info':
        return <Info className="w-4 h-4 text-blue-600" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-purple-600" />;
    }
  };

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sistem Logları</h1>
            <p className="text-sm text-gray-600 mt-1">
              Tüm sistem işlemlerini detaylı olarak görüntüleyin
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Dışa Aktar
            </button>
          </div>
        </div>

        {/* İstatistikler */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Log</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString('tr-TR')}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Son 24 Saat</p>
                <p className="text-2xl font-bold text-gray-900">{stats.last24h.toLocaleString('tr-TR')}</p>
              </div>
              <Clock className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Son 7 Gün</p>
                <p className="text-2xl font-bold text-gray-900">{stats.last7d.toLocaleString('tr-TR')}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Başarısız İşlem</p>
                <p className="text-2xl font-bold text-red-600">{stats.failures.toLocaleString('tr-TR')}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Başarı Oranı</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.total > 0 ? ((stats.total - stats.failures) / stats.total * 100).toFixed(1) : 0}%
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        )}

        {/* Arama ve Filtreler */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Log ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:w-[290px]">
              <label className="text-xs text-gray-600">
                Başlangıç
                <input
                  type="date"
                  value={dateRange.start}
                  max={dateRange.end || undefined}
                  onChange={(e) => {
                    setDateRange(prev => ({ ...prev, start: e.target.value }));
                    setPage(1);
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="text-xs text-gray-600">
                Bitiş
                <input
                  type="date"
                  value={dateRange.end}
                  min={dateRange.start || undefined}
                  onChange={(e) => {
                    setDateRange(prev => ({ ...prev, end: e.target.value }));
                    setPage(1);
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                showFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtreler
              {(selectedCategories.length + selectedActions.length + selectedSeverity.length) > 0 && (
                <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {selectedCategories.length + selectedActions.length + selectedSeverity.length}
                </span>
              )}
            </button>
          </div>

          {/* Filtre Paneli */}
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 gap-6 border-t border-gray-200 pt-4 lg:grid-cols-3">
              {/* Kategori Filtreleri */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Kategori</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(AUDIT_CATEGORY_INFO).map(([key, info]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(key as AuditLogCategory)}
                        onChange={() => toggleCategory(key as AuditLogCategory)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className={`text-xs px-2 py-1 rounded-md ${info.color}`}>
                        {info.icon} {info.label}
                      </span>
                      <span className="text-xs text-gray-500 ml-auto">
                        ({stats.byCategory[key as AuditLogCategory] || 0})
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Aksiyon Filtreleri */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">İşlem Tipi</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(AUDIT_ACTION_INFO).map(([key, info]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedActions.includes(key as AuditLogAction)}
                        onChange={() => toggleAction(key as AuditLogAction)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className={`text-sm ${info.color}`}>
                        {info.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Önem Derecesi Filtreleri */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Önem Derecesi</p>
                <div className="space-y-2">
                    {Object.entries(AUDIT_SEVERITY_INFO).map(([key, info]) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={selectedSeverity.includes(key as AuditLogSeverity)}
                          onChange={() => toggleSeverity(key as AuditLogSeverity)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className={`text-xs px-2 py-1 rounded-md border ${info.color}`}>
                          {info.icon} {info.label}
                        </span>
                        <span className="text-xs text-gray-500 ml-auto">
                          ({stats.bySeverity[key as AuditLogSeverity] || 0})
                        </span>
                      </label>
                    ))}
                  </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Log Listesi */}
        <div className="bg-white rounded-lg border border-gray-200 min-w-0">
          <div className="flex flex-col items-start justify-between gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center">
          <h2 className="font-semibold text-gray-900">
            Log Kayıtları ({pagination.total.toLocaleString('tr-TR')})
          </h2>
          <button
            onClick={() => void loadLogs()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Yükleniyor' : 'Yenile'}
          </button>
        </div>

        <div className="divide-y divide-gray-200 max-h-[800px] overflow-y-auto">
          {logs.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Gösterilecek log kaydı bulunamadı</p>
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  !log.success ? 'bg-red-50' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Severity Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getSeverityIcon(log.severity)}
                  </div>

                  {/* Log Detayları */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-md ${getCategoryInfo(log.category).color}`}>
                          {getCategoryInfo(log.category).icon} {getCategoryInfo(log.category).label}
                        </span>
                        <span className={`text-xs font-medium ${getActionInfo(log.action).color}`}>
                          {getActionInfo(log.action).label}
                        </span>
                        {log.entityName && (
                          <span className="text-xs text-gray-600">
                            • {log.entityName}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {formatDate(log.timestamp)}
                      </span>
                    </div>

                    <p className="text-sm text-gray-900 mb-1">{log.description}</p>

                      <div className="flex items-center gap-4 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.userName}
                      </span>
                        {log.entityId && (
                          <span className="text-gray-400">ID: {log.entityId}</span>
                        )}
                        {log.ipAddress && (
                          <span className="text-gray-400">IP: {log.ipAddress}</span>
                        )}
                      </div>

                      {log.category === 'SECURITY' && log.metadata && (
                        <div className="mt-2 text-xs text-gray-600">
                          {log.metadata.method && <span>{String(log.metadata.method)} </span>}
                          {log.metadata.path && <span>{String(log.metadata.path)}</span>}
                        </div>
                      )}

                    {/* Değişiklikler */}
                    {log.changes && log.changes.length > 0 && (
                      <div className="mt-2 p-2 bg-gray-100 rounded-md">
                        <p className="text-xs font-medium text-gray-700 mb-1">Değişiklikler:</p>
                        <div className="space-y-1">
                          {log.changes.map((change, idx) => (
                            <div key={idx} className="text-xs text-gray-600">
                              <span className="font-medium">{change.field}:</span>
                              <span className="text-red-600 mx-1">{JSON.stringify(change.oldValue)}</span>
                              →
                              <span className="text-green-600 mx-1">{JSON.stringify(change.newValue)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hata mesajı */}
                    {!log.success && log.errorMessage && (
                      <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded-md">
                        <p className="text-xs text-red-700">
                          <span className="font-medium">Hata:</span> {log.errorMessage}
                        </p>
                      </div>
                    )}
                </div>
              </div>
              </div>
            ))
          )}
        </div>
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm">
            <button
              onClick={() => setPage(current => Math.max(1, current - 1))}
              disabled={page === 1 || loading}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Önceki
            </button>
            <span className="text-gray-600">Sayfa {page} / {pagination.totalPages}</span>
            <button
              onClick={() => setPage(current => Math.min(pagination.totalPages, current + 1))}
              disabled={page === pagination.totalPages || loading}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Sonraki
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
